// src/components/Form/employeelist.jsx
// EmployeeList.jsx - Updated: Removed attendance modal and functionality. Row click now navigates to /employee-details/:id for full details page.
// Kept details modal for View Details button. Enhanced Schedule & Other section in details modal to fetch shift timing from active schedule assignment.
// Also ensures special days display from assignments in modal. Table columns for shiftTiming and specialDays are optional via column management.
// UPDATED: Removed salutation and dateOfBirth from default columns; they are available in possibleColumns for optional addition.
// UPDATED: Handle multiple time_slots in shifts for shiftTiming display in table and modal (split shifts).
// NEW: Added Delivery Profile section in details modal to display linked delivery employee details (secretKey, vehicleNumber, role, etc.) if deliveryProfile exists in employee data.
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaUserTie, FaArrowLeft, FaEdit, FaTrash, FaPlus, FaTimes, FaClock, FaSearch, FaFilter, FaEye, FaCalendarCheck, FaGift, FaTruck, FaKey, FaChevronDown, FaChevronUp, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { UserContext } from '../../Context/UserContext';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';

const EmployeeList = ({ permissions }) => {
    const navigate = useNavigate();
    const { user, baseUrl, configLoading, getHeaders } = useContext(UserContext);
    const [employeesList, setEmployeesList] = useState([]);
    const [employeeDesignations, setEmployeeDesignations] = useState([]); // New: Fetch designations for dropdown filter
    const [employeeTypes, setEmployeeTypes] = useState([]); // New: Fetch types for dropdown filter
    // NEW: Schedule Data States
    const [assignments, setAssignments] = useState([]);
    const [scheduleRules, setScheduleRules] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsEmployee, setDetailsEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [currency, setCurrency] = useState('$');

    const [canRead, setCanRead] = useState(false);
    const [canWrite, setCanWrite] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canCreate, setCanCreate] = useState(false);
    const [dataAccess, setDataAccess] = useState('ALL');

    // Multi-Company State
    const [permsLoading, setPermsLoading] = useState(true);
    const [permModalMsg, setPermModalMsg] = useState("");
    const [showPermModal, setShowPermModal] = useState(false);
    const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
    const isGlobalMaster = checkIsGlobalAdmin(user) || checkIsGlobalAdmin({ role: localStorage.getItem('role') });
    const [availableBranches, setAvailableBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(location.state?.branch || localStorage.getItem('active_branch') || '');
    const [selectedCompany, setSelectedCompany] = useState('All'); // NEW: Company filter
    const [companyOptions, setCompanyOptions] = useState([]); // NEW: Company options
    const [accessibleDesignations, setAccessibleDesignations] = useState([]); // NEW: For hierarchy access
    const [doctypeFields, setDoctypeFields] = useState([]);

    const fetchDoctype = async (currentBaseUrl = baseUrl) => {
        try {
            const response = await axios.get(`${currentBaseUrl}/api/doctypes/Employee`, { headers: getHeaders() });
            setDoctypeFields(response.data.fields || []);
        } catch (e) {
            console.error("Error fetching Employee doctype:", e);
        }
    };

    // Fetch Permissions
    const fetchPermissions = async (currentBaseUrl = baseUrl) => {
        try {
            setPermsLoading(true);
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userObj = JSON.parse(userStr);
                const roleRaw = userObj.role || '';
                // NEW: Populate company options for group_admin
                if (checkIsGlobalAdmin(userObj) && userObj.companies) {
                    setCompanyOptions(userObj.companies);
                    const activeCtx = localStorage.getItem('active_company');
                    if (activeCtx) setSelectedCompany(activeCtx);
                }

                // Group Admin should be treated as a Company Admin for visibility purposes
                const isAdmin = checkIsAdmin(userObj);
                setIsCompanyAdmin(isAdmin);
                if (roleRaw) {
                    const userEmail = userObj.email || '';
                    const url = `${currentBaseUrl}/api/role-permissions?role=${encodeURIComponent(roleRaw)}&email=${userEmail}`;
                    const response = await axios.get(url, { headers: getHeaders() });
                    const perms = response.data.permissions || [];
                    const accDesigs = response.data.accessible_designations || [];
                    setAccessibleDesignations(accDesigs);

                    const pagePerm = perms.find(p => p.pageId === 'employee_list');
                    const isSuperAdmin = checkIsGlobalAdmin(userObj);

                    if (isSuperAdmin || isAdmin) {
                        // Administrative/Super bypass
                        setCanRead(true);
                        setCanWrite(true);
                        setCanDelete(true);
                        setCanCreate(true);
                        setDataAccess('ALL');
                    } else if (pagePerm) {
                        setCanRead(pagePerm.canRead === true);
                        setCanWrite(pagePerm.canWrite === true);
                        setCanCreate(pagePerm.canCreate === true);
                        setDataAccess(pagePerm.dataAccess || 'ALL');
                    }
                    return accDesigs; // Return for sequential fetching
                }
            }
            return [];
        } catch (error) {
            return [];
        } finally {
            setPermsLoading(false);
        }
    };
    // Updated: Filter states - designation and type now use dropdown selections
    const [filters, setFilters] = useState({
        name: '',
        designation: '',
        type: '',
        phone: '',
        salary: '',
        employeeId: ''
    });
    // NEW: Column management states
    const [columnOrder, setColumnOrder] = useState([
        { key: "employeeId", label: "Employee ID", align: "left" },
        { key: "name", label: "Full Name", align: "left" },
        // REMOVED: salutation and dateOfBirth from default - now optional via add columns
        { key: "phoneNumber", label: "Phone", align: "left" },
        { key: "email", label: "Email", align: "left" },
        { key: "employeeDesignation", label: "Designation", align: "left" },
        { key: "employeeType", label: "Type", align: "left" },
        { key: "company", label: "Company", align: "left" }, // ADDED: Multi-company visibility
        { key: "branch_name", label: "Branch", align: "left" }, // NEW: Branch visibility
        { key: "shiftTiming", label: "Shift Timing", align: "left" }, // NEW: Default column
        { key: "status", label: "Status", align: "center" },
        { key: "actions", label: "Actions", align: "center" },
    ]);
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFieldToAdd, setSelectedFieldToAdd] = useState('');
    const [selectedPosition, setSelectedPosition] = useState(0);
    // NEW: All possible columns (excluding actions) - Now includes dynamic doctype fields
    const possibleColumns = React.useMemo(() => {
        const staticCols = [
            { key: "employeeId", label: "Employee ID", align: "left" },
            { key: "profile", label: "Profile", align: "center" },
            { key: "name", label: "Full Name", align: "left" },
            { key: "salutation", label: "Salutation", align: "left" }, // Now optional
            { key: "gender", label: "Gender", align: "left" },
            { key: "dateOfBirth", label: "DOB", align: "left" }, // Now optional
            { key: "phoneNumber", label: "Phone", align: "left" },
            { key: "email", label: "Email", align: "left" },
            { key: "address", label: "Address", align: "left" },
            { key: "nationality", label: "Nationality", align: "left" },
            { key: "maritalStatus", label: "Marital Status", align: "left" },
            { key: "employeeDesignation", label: "Designation", align: "left" },
            { key: "employeeType", label: "Type", align: "left" },
            { key: "status", label: "Status", align: "center" },
            { key: "company", label: "Company", align: "left" },
            { key: "branch_name", label: "Branch", align: "left" },
            { key: "dateOfJoining", label: "Joined", align: "left" },
            { key: "idNumber", label: "ID Number", align: "left" },
            { key: "idExpiry", label: "ID Expiry", align: "left" },
            { key: "bankName", label: "Bank Name", align: "left" },
            { key: "accountHolderName", label: "Acc Holder", align: "left" },
            { key: "accountNumber", label: "Acc Number", align: "left" },
            { key: "branchCode", label: "Branch Code", align: "left" },
            { key: "basicSalary", label: "Basic Salary", align: "right" },
            { key: "hra", label: "HRA", align: "right" },
            { key: "ta", label: "TA", align: "right" },
            { key: "oa", label: "OA", align: "right" },
            { key: "totalSalary", label: "Total Salary", align: "right" },
            { key: "startTime", label: "Start Time", align: "left" },
            { key: "endTime", label: "End Time", align: "left" },
            { key: "specialTimings", label: "Special Timings", align: "center" },
            { key: "education", label: "Education", align: "left" },
            { key: "previousExperience", label: "Experience", align: "left" },
            { key: "skills", label: "Skills", align: "left" },
            { key: "healthInfo", label: "Health Info", align: "left" },
            { key: "familyDetails", label: "Family Details", align: "left" },
            { key: "username", label: "Username", align: "left" },
            { key: "shiftTiming", label: "Shift Timing", align: "left" },
            { key: "specialDays", label: "Special Days", align: "left" }, // NEW: Added for schedule assignments special days
            { key: "created_at", label: "Created At", align: "left" },
        ];

        const dynamicCols = doctypeFields
            .filter(f => !staticCols.some(sc => sc.key === f.id) && f.type !== 'Password' && f.id !== 'profile_image')
            .map(f => ({
                key: f.id,
                label: f.label,
                align: f.type === 'Number' ? 'right' : 'left'
            }));

        return [...staticCols, ...dynamicCols];
    }, [doctypeFields]);
    // Fetch baseUrl on component mount
    useEffect(() => {
        if (!configLoading) {
            const url = baseUrl || "";
            const initData = async () => {
                const fetchedDesigs = await fetchPermissions(url);
                Promise.all([
                    fetchCurrency(url),
                    fetchEmployees(url, fetchedDesigs),
                    fetchEmployeeDesignations(url, fetchedDesigs),
                    fetchEmployeeTypes(url),
                    fetchScheduleData(url),
                    fetchBranches(url),
                    fetchDoctype(url)
                ]);
            };
            initData();
        }
    }, [configLoading, baseUrl]);

    // NEW: Refetch employees when branch or company filter changes
    useEffect(() => {
        if (!configLoading && baseUrl) {
            fetchEmployees(baseUrl);
            fetchScheduleData(baseUrl);
        }
    }, [selectedBranch, selectedCompany]);
    // Fetch currency from system settings
    const fetchCurrency = async (currentBaseUrl = baseUrl) => {
        try {
            const url = `${currentBaseUrl}/api/settings`;
            const response = await axios.get(url, { headers: getHeaders() });
            const settingsData = response.data;
            const currencyCode = settingsData.currency || 'USD';
            const currencySymbol = getCurrencySymbol(currencyCode);
            setCurrency(currencySymbol);
        } catch (err) {
            console.error('Error fetching currency settings:', err);
            setCurrency('$');
        }
    };
    // Helper function to get currency symbol based on code
    const getCurrencySymbol = (code) => {
        const symbols = {
            'USD': '$',
            'INR': '₹',
            'EUR': '€',
            'GBP': '£',
            'JPY': '¥',
            'AUD': 'A$',
            'CAD': 'C$',
            'AED': 'AED',
        };
        return symbols[code] || code;
    };
    // NEW: Fetch Schedule Data (Assignments, Rules, Shifts)
    const fetchScheduleData = async (currentBaseUrl = baseUrl, activeCompany = null) => {
        try {
            // UPDATED: Use unified /api/schedules for both rules and shifts
            const userStr = localStorage.getItem('user');
            const userData = userStr ? JSON.parse(userStr) : {};
            const companyToHeader = activeCompany || ((selectedCompany === 'All' && !isGlobalMaster)
                ? (userData.company_name || userData.company)
                : selectedCompany);

            const headers = getHeaders(selectedBranch, companyToHeader);

            let assignRes = { data: [] };
            let schedRes = { data: [] };

            try { assignRes = await axios.get(`${currentBaseUrl}/api/schedule-assignments`, { params: { company: companyToHeader, branch: selectedBranch, pageId: 'schedule_assign' }, headers }); } catch (e) { console.error("Failed to fetch assignments", e); }
            try { schedRes = await axios.get(`${currentBaseUrl}/api/schedules`, { params: { company: companyToHeader, branch: selectedBranch, pageId: 'schedule_master' }, headers }); } catch (e) { console.error("Failed to fetch schedules", e); }

            setAssignments(assignRes.data || []);
            // Both state variables get the unified list
            setScheduleRules(schedRes.data || []);
            setShifts(schedRes.data || []);
        } catch (err) {
            console.error("Error fetching schedule data:", err);
        }
    };
    const fetchEmployees = async (currentBaseUrl = baseUrl, overrideDesigs = null) => {
        try {
            setLoading(true);

            const userStr = localStorage.getItem('user');
            const userData = userStr ? JSON.parse(userStr) : {};
            const userRole = user?.role || userData.role || '';

            // RESOLUTION: If 'All' is selected, determine if the user is authorized for an aggregated view.
            // Global masters (superadmin/group_admin) can send 'All'. Standard admins fall back to their primary company
            // to avoid "Access Denied" and ensure strict isolation.
            const companyToHeader = (selectedCompany === 'All' && !isGlobalMaster)
                ? (userData.company_name || userData.company)
                : selectedCompany;

            const headers = getHeaders(selectedBranch, companyToHeader);
            const url = `${currentBaseUrl}/api/employees`;
            const userId = user?.userId || user?._id || user?.id;

            console.log(`FETCHING EMPLOYEES: Company=${companyToHeader}, Branch=${selectedBranch}, Role=${userRole}`);

            const response = await axios.get(url, {
                headers,
                params: {
                    userId: userId,
                    id: userId,
                    role: userRole,
                    branch_name: selectedBranch,
                    pageId: 'employee_list'
                }
            });

            console.log("FETCHED EMPLOYEES DATA:", response.data);
            console.log("EMPLOYEE COUNT:", response.data.length);

            if (Array.isArray(response.data)) {
                // Ensure we filter out any potential drafts and normalize IDs
                let validEmployees = response.data.map(emp => ({
                    ...emp,
                    id: emp._id || emp.id
                })).filter(emp => !emp.isDraft);

                // Apply hierarchy filtering for non-admins if accessibleDesignations exists
                // FIXED: Only bypass for system-wide masters (Group/Super Admin)
                const isActuallyAdmin = checkIsGlobalAdmin(userData) || (String(userData.role).toLowerCase() === 'company_admin');
                const activeDesigs = overrideDesigs !== null ? overrideDesigs : accessibleDesignations;

                if (!isActuallyAdmin && activeDesigs && activeDesigs.length > 0) {
                    const freshUser = JSON.parse(localStorage.getItem('user') || '{}');
                    const currentUserId = freshUser.userId || freshUser._id || freshUser.id;
                    const currentUserEmpId = freshUser.employeeId || freshUser.employee_id;
                    const normalizedAcc = activeDesigs.map(d => String(d).trim().toLowerCase());

                    console.log("Applying frontend hierarchy filter with rules:", normalizedAcc);

                    validEmployees = validEmployees.filter(emp => {
                        if (!emp) return false;
                        // Always allow self (robust matching by ID, employeeId, email, or username)
                        const isSelf = (
                            emp._id === currentUserId ||
                            emp.id === currentUserId ||
                            emp.employeeId === currentUserEmpId ||
                            (emp.email && freshUser.email && String(emp.email).toLowerCase() === String(freshUser.email).toLowerCase()) ||
                            (emp.username && freshUser.username && String(emp.username).toLowerCase() === String(freshUser.username).toLowerCase())
                        );
                        if (isSelf) return true;

                        const empDesig = String(emp.employeeDesignation || emp.designation || '').trim().toLowerCase();
                        return normalizedAcc.includes(empDesig);
                    });
                } else {
                    console.log("Hierarchy filter bypassed: User is Admin or no restrictions defined.");
                }

                console.log("FINAL VALID EMPLOYEES:", validEmployees.length);
                setEmployeesList(validEmployees);
            } else {
                console.error("Unexpected API response format:", response.data);
                setEmployeesList([]);
            }
            setError('');
        } catch (err) {
            console.error('Error fetching employees:', err);
            // Detailed error logging
            if (err.response?.status === 403) {
                setError('Access Denied: You do not have permission to view employees in this company/branch.');
            } else {
                setError(`Failed to fetch employees: ${err.response?.data?.error || err.message}`);
            }
        } finally {
            setLoading(false);
        }
    };
    // New: Fetch employee designations for dropdown filter
    const fetchEmployeeDesignations = async (currentBaseUrl = baseUrl, overrideDesigs = null) => {
        try {
            const response = await axios.get(`${currentBaseUrl}/api/employee-designations`, { headers: getHeaders() });

            let allDesigs = response.data || [];
            const activeDesigs = overrideDesigs !== null ? overrideDesigs : accessibleDesignations;
            // Filter designations by hierarchy
            if (!isCompanyAdmin && activeDesigs.length > 0) {
                const freshUser = JSON.parse(localStorage.getItem('user') || '{}');
                const myTokens = [
                    String(freshUser.designation || '').trim().toLowerCase(),
                    String(freshUser.employeeDesignation || '').trim().toLowerCase(),
                    String(freshUser.role || '').trim().toLowerCase()
                ].filter(t => t !== '');

                const normalizedAcc = activeDesigs.map(d => String(d).trim().toLowerCase());

                allDesigs = allDesigs.filter(d => {
                    const dName = String(d.name || '').trim().toLowerCase();
                    return normalizedAcc.includes(dName) || myTokens.includes(dName);
                });
            }
            setEmployeeDesignations(allDesigs);
        } catch (err) {
            console.error('Error fetching employee designations:', err);
        }
    };
    const fetchEmployeeTypes = async (currentBaseUrl = baseUrl) => {
        try {
            const response = await axios.get(`${currentBaseUrl}/api/employee-types`, { headers: getHeaders() });
            setEmployeeTypes(response.data);
        } catch (err) {
            console.error('Error fetching employee types:', err);
        }
    };

    const fetchBranches = async (currentBaseUrl = baseUrl) => {
        try {
            const response = await axios.get(`${currentBaseUrl}/api/branches`, { headers: getHeaders() });
            const branchData = response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
            setAvailableBranches(branchData);

            // NEW: For branch-scoped users, auto-select their branch if not already set
            const userStr2 = localStorage.getItem('user');
            if (userStr2) {
                const userData = JSON.parse(userStr2);
                const normRole = (userData.role || '').toLowerCase().replace(/[\s_]/g, '');
                const isMaster = normRole === 'companyadmin' || normRole === 'groupadmin' || normRole === 'superadmin';

                const profileBranch = userData.branch_name || userData.branch || userData.pos_profile;

                if (!isMaster && profileBranch && !selectedBranch) {
                    const match = branchData.find(b => String(b).toLowerCase() === String(profileBranch).toLowerCase());
                    if (match) setSelectedBranch(match);
                    else if (String(profileBranch).toLowerCase() !== 'all') setSelectedBranch(profileBranch);
                }
            }
        } catch (e) {
            console.error("Failed to fetch branches:", e);
        }
    };
    // Updated: Filter employees based on filter states - exact match for dropdown fields
    const filteredEmployees = (employeesList || []).filter((emp) => {
        if (!emp) return false;
        const nameMatch = (emp.name || '').toLowerCase().includes((filters.name || '').toLowerCase());
        const designationMatch = (filters.designation === '' || emp.employeeDesignation === filters.designation);
        const typeMatch = (filters.type === '' || emp.employeeType === filters.type);
        const phoneMatch = (emp.phoneNumber || '').includes(filters.phone || '');
        const salaryMatch = String(emp.totalSalary || '').includes(filters.salary || '');
        const idMatch = (emp.employeeId || '').toLowerCase().includes((filters.employeeId || '').toLowerCase());

        return nameMatch && designationMatch && typeMatch && phoneMatch && salaryMatch && idMatch;
    });
    // Updated: Handle filter changes - for dropdowns, set exact value
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };
    // New: Clear all filters
    const clearFilters = () => {
        setFilters({
            name: '',
            designation: '',
            type: '',
            phone: '',
            salary: '',
            employeeId: ''
        });
    };
    // UPDATED: Handle click on employee row - Now navigates to details page instead of attendance modal
    const handleEmployeeClick = (emp) => {
        navigate(`/employee-details/${emp._id}`);
    };
    const handleEditEmployee = (emp) => {
        if (!canWrite) {
            setPermModalMsg("You do not have permission to edit employees.");
            setShowPermModal(true);
            return;
        }
        navigate('/add-employee', { state: { editingEmployee: emp } });
    };
    const handleViewDetails = async (emp) => {
        // Fetch full details to ensure deliveryProfile is included (backend provides it in GET /api/add-employee/<id>)
        try {
            // UPDATED: Use /api/employees/ and prefer employeeId
            const idToFetch = emp.employeeId || emp._id;
            const url = baseUrl ? `${baseUrl}/api/employees/${idToFetch}` : `/api/employees/${idToFetch}`;
            const response = await axios.get(url);
            const fullEmp = response.data;
            setDetailsEmployee(fullEmp);
        } catch (err) {
            console.error('Error fetching employee details:', err);
            setDetailsEmployee(emp); // Fallback to cached data
        }
        setShowDetailsModal(true);
    };
    const handleDeleteEmployee = (id) => {
        if (!canDelete) {
            setPermModalMsg("You do not have permission to delete employees.");
            setShowPermModal(true);
            return;
        }
        setDeletingId(id);
        setShowDeleteConfirm(true);
    };
    const confirmDeleteEmployee = async () => {
        try {
            setLoading(true);
            // UPDATED: Use /api/employees/ for deletion
            const url = baseUrl ? `${baseUrl}/api/employees/${deletingId}` : `/api/employees/${deletingId}`;
            const response = await fetch(url, {
                method: 'DELETE',
                headers: getHeaders(),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete employee');
            }
            await fetchEmployees();
            setMessage('Employee deleted successfully!');
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete employee.');
        } finally {
            setDeletingId(null);
            setShowDeleteConfirm(false);
            setLoading(false);
        }
    };
    const closeDeleteConfirm = (e) => {
        if (e.target === e.currentTarget) {
            setShowDeleteConfirm(false);
            setDeletingId(null);
        }
    };
    // NEW: Column management functions
    const addColumn = () => {
        const fieldKey = selectedFieldToAdd;
        if (!fieldKey) return;
        const field = possibleColumns.find(p => p.key === fieldKey);
        if (!field || columnOrder.some(c => c.key === field.key)) return;
        const pos = parseInt(selectedPosition);
        const newOrder = [...columnOrder];
        newOrder.splice(pos, 0, field);
        setColumnOrder(newOrder);
        setSelectedFieldToAdd('');
        setSelectedPosition(0);
    };
    const removeColumn = (index) => {
        const newOrder = [...columnOrder];
        const removed = newOrder.splice(index, 1)[0];
        // If actions removed, add it back at the end
        if (removed && removed.key === "actions") {
            newOrder.push(removed);
        }
        setColumnOrder(newOrder);
    };
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData("text/plain", index);
        e.target.style.backgroundColor = 'rgba(52, 152, 219, 0.2)';
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.target.style.backgroundColor = 'rgba(46, 204, 113, 0.2)';
    };
    const handleDragLeave = (e) => {
        e.target.style.backgroundColor = '';
    };
    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
        const newOrder = [...columnOrder];
        const [draggedColumn] = newOrder.splice(sourceIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);
        setColumnOrder(newOrder);
        // Reset styles
        document.querySelectorAll('table th').forEach(th => {
            th.style.backgroundColor = '';
        });
    };
    const handleDragEnd = (e) => {
        document.querySelectorAll('table th').forEach(th => {
            th.style.backgroundColor = '';
        });
    };
    const thStyle = {
        padding: '15px 12px',
        border: 'none',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        fontWeight: '600',
        fontSize: '0.95rem'
    };
    const tdStyle = {
        padding: '15px 12px',
        borderRight: '1px solid #e9ecef',
        whiteSpace: 'nowrap',
        color: '#2c3e50'
    };
    const tdStyleCenter = {
        ...tdStyle,
        textAlign: 'center'
    };
    const actionBtnStyle = {
        padding: '6px 10px',
        color: 'white',
        border: 'none',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        transition: 'all 0.3s ease'
    };
    // Helper to get shift timing string (handles single or multiple time_slots)
    const getShiftTimingString = (shift) => {
        if (!shift) return 'N/A';

        // Defensive parsing for time_slots (could be string if imported from Excel)
        let timeSlots = shift.time_slots;
        if (typeof timeSlots === 'string') {
            try {
                timeSlots = JSON.parse(timeSlots);
            } catch (e) {
                timeSlots = [];
            }
        }

        if (Array.isArray(timeSlots) && timeSlots.length > 0) {
            // Multiple slots (split shift)
            const slotStr = timeSlots.map(s =>
                `${s.start_time || ''}-${s.end_time || ''}${s.is_overnight ? ' (O)' : ''}`
            ).join(', ');
            return `${shift.schedule_name || 'Shift'} (${slotStr})`;
        } else {
            // Legacy single slot
            return `${shift.schedule_name || 'Shift'} ${shift.start_time || ''}${shift.start_time && shift.end_time ? ' - ' : ''}${shift.end_time || ''}`;
        }
    };
    // Helper to get cell content based on column key - UPDATED: Handle time_slots for shiftTiming
    const getCellContent = (emp, col) => {
        // Helper to find active assignment for this employee
        const getActiveAssignment = (emp) => {
            if (!emp) return null;
            const empId = emp._id || emp.id;
            const employeeId = emp.employeeId;
            return assignments.find(a =>
                (String(a.employee_id) === String(empId) || String(a.employee_id) === String(employeeId)) &&
                (a.is_active === true || String(a.is_active) === 'true')
            );
        };
        switch (col.key) {
            case 'profile':
                return (
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        margin: '0 auto',
                        border: '2px solid #ecf0f1'
                    }}>
                        {emp.profileImage ? (
                            <img src={emp.profileImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <div style={{ width: '100%', height: '100%', background: '#bdc3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px' }}>
                                {emp.name ? emp.name.charAt(0).toUpperCase() : '?'}
                            </div>
                        )}
                    </div>
                );
            case 'status':
                return (
                    <span style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        backgroundColor: emp.status === 'Active' ? '#d4edda' : '#f8d7da',
                        color: emp.status === 'Active' ? '#155724' : '#721c24'
                    }}>
                        {emp.status || 'Active'}
                    </span>
                );
            case 'employeeDesignation':
                return <span style={{ color: '#3498db', fontWeight: '500' }}>{emp.employeeDesignation}</span>;
            case 'employeeType':
                return <span style={{ color: '#27ae60', fontWeight: '500' }}>{emp.employeeType}</span>;
            case 'totalSalary':
                return <span style={{ color: '#e67e22', fontWeight: '600' }}>{currency}{emp.totalSalary}</span>;
            case 'basicSalary':
            case 'hra':
            case 'ta':
            case 'oa':
                return `${currency}${emp[col.key]}`;
            case 'specialTimings':
                let sT = emp.specialTimings;
                if (typeof sT === 'string') {
                    try {
                        sT = JSON.parse(sT);
                    } catch (e) {
                        sT = [];
                    }
                }
                return Array.isArray(sT) ? sT.length : 0;
            case 'shiftTiming':
                // Match employee to active assignment -> rule -> shift
                const assignForShift = getActiveAssignment(emp);
                if (!assignForShift) return <span style={{ color: '#95a5a6', fontStyle: 'italic' }}>Unassigned</span>;

                const ruleForShift = scheduleRules.find(r => String(r._id || r.id) === String(assignForShift.schedule_id));
                if (!ruleForShift) return <span style={{ color: '#e74c3c' }}>Rule Missing</span>;

                // Check if rule has a linked shift or is self-contained
                let shiftForTiming = null;
                if (ruleForShift.shift_id) {
                    shiftForTiming = shifts.find(s => String(s._id || s.id) === String(ruleForShift.shift_id));
                } else {
                    // Self-contained: use rule itself as the source of timing
                    shiftForTiming = ruleForShift;
                }

                if (!shiftForTiming) return <span style={{ color: '#e74c3c' }}>Shift Missing</span>;

                const timingStr = getShiftTimingString(shiftForTiming);
                return (
                    <div style={{ whiteSpace: 'normal' }}>
                        <div style={{ fontWeight: '600', color: '#2c3e50' }}>{timingStr}</div>
                        <div style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>{ruleForShift.schedule_name}</div>
                    </div>
                );
            case 'specialDays': // NEW: Display special day assignments from active schedule assignment
                const assign = getActiveAssignment(emp);
                if (!assign) return <span style={{ color: '#95a5a6', fontStyle: 'italic' }}>None</span>;

                let specialDays = assign.special_day_assignments;
                if (typeof specialDays === 'string') {
                    try {
                        specialDays = JSON.parse(specialDays);
                    } catch (e) {
                        specialDays = [];
                    }
                }

                if (!Array.isArray(specialDays) || specialDays.length === 0) {
                    return <span style={{ color: '#95a5a6', fontStyle: 'italic' }}>None</span>;
                }

                return (
                    <div style={{ maxHeight: '80px', overflowY: 'auto', fontSize: '0.8rem' }}>
                        {specialDays.slice(0, 3).map((sd, idx) => (
                            <div key={idx} style={{
                                marginBottom: '4px',
                                padding: '4px 6px',
                                background: '#f0f8ff',
                                borderRadius: '4px',
                                borderLeft: `3px solid ${sd.type === 'Holiday' ? '#e74c3c' : sd.type === 'Half-Day' ? '#f39c12' : '#9b59b6'}`
                            }}>
                                <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                                    {sd.date} <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>({sd.type})</span>
                                </div>
                                <div style={{ color: '#34495e', fontSize: '0.75rem' }}>{sd.description}</div>
                                {sd.type === 'Half-Day' && <div style={{ fontSize: '0.7rem', color: '#f39c12' }}><FaClock /> {sd.start_time} - {sd.end_time}</div>}
                                {sd.type === 'Extended' && <div style={{ fontSize: '0.7rem', color: '#9b59b6' }}><FaClock /> {sd.extended_start} - {sd.extended_end}</div>}
                            </div>
                        ))}
                        {specialDays.length > 3 && (
                            <div style={{ fontSize: '0.7rem', color: '#7f8c8d', textAlign: 'center', marginTop: '4px' }}>
                                +{specialDays.length - 3} more
                            </div>
                        )}
                    </div>
                );
            case 'branch_name':
            case 'branch':
                return <span style={{ color: '#e67e22', fontWeight: '500' }}>{emp.branch_name || emp.branch || 'Unassigned'}</span>;
            case 'created_at':
                return emp.created_at ? new Date(emp.created_at).toLocaleString() : '';
            case 'actions':
                return (
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(emp); }}
                            style={{
                                padding: '6px 10px',
                                background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(46, 204, 113, 0.3)'
                            }}
                            title="View Details"
                        >
                            <FaEye />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleEditEmployee(emp); }}
                            style={{
                                padding: '6px 10px',
                                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(52, 152, 219, 0.3)'
                            }}
                            title="Edit"
                        >
                            <FaEdit />
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(emp.employeeId || emp._id); }}
                            style={{
                                padding: '6px 10px',
                                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 4px rgba(231, 76, 60, 0.3)'
                            }}
                            title="Delete"
                        >
                            <FaTrash />
                        </button>
                    </div>
                );
            default:
                return emp[col.key] || 'N/A';
        }
    };
    const addNewEmployee = () => {
        if (!canCreate) {
            setPermModalMsg("You do not have permission to add new employees.");
            setShowPermModal(true);
            return;
        }
        navigate('/add-employee');
    };
    if (permsLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
                <div style={{ textAlign: 'center', color: '#fff', fontSize: '1.2rem', fontWeight: '600' }}>
                    <FaUserTie style={{ fontSize: '48px', marginBottom: '20px', color: '#fff' }} />
                    <p>Loading Permissions...</p>
                </div>
            </div>
        );
    }

    if (!canRead) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
                <div style={{ textAlign: 'center', background: '#fff', padding: '40px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', maxWidth: '500px' }}>
                    <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>Access Denied</h2>
                    <p style={{ color: '#7f8c8d', fontSize: '1.1rem' }}>You do not have permission to view the Employee List.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/admin')} style={{ marginTop: '20px', borderRadius: '50px', padding: '10px 30px', background: '#3498db', border: 'none', color: '#fff' }}>Back to Admin</button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)'
            }}>
                <div style={{
                    textAlign: 'center',
                    color: '#3498db',
                    fontSize: '18px'
                }}>
                    <FaUserTie style={{ fontSize: '48px', marginBottom: '20px', color: '#3498db' }} />
                    <p>Loading employees...</p>
                </div>
            </div>
        );
    }
    return (
        <div className="employee-list-page" style={{ 
            backgroundColor: "#F8F9FD", 
            height: "100vh", 
            display: "flex", 
            flexDirection: "column", 
            overflow: "hidden", 
            fontFamily: "'Gilroy', 'Outfit', 'Inter', sans-serif",
            color: "#1B1B29"
        }}>
            <style>
            {`
            @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
            @import url('https://fonts.cdnfonts.com/css/gilroy-bold');
            
            .premium-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
            }
            .premium-table th {
              background: #06D6A0;
              color: #ffffff;
              font-weight: 600;
              font-size: 15px;
              text-transform: capitalize;
              letter-spacing: 0.3px;
              padding: 16px 24px;
              text-align: left;
              border: none;
              position: sticky;
              top: 0;
              z-index: 10;
            }
            .premium-table th:first-child {
              border-top-left-radius: 10px;
            }
            .premium-table th:last-child {
              border-top-right-radius: 10px;
            }
            .premium-table td {
              padding: 16px 24px;
              color: #4A4A5A;
              font-size: 14px;
              font-weight: 500;
              border-bottom: 1px solid #F1F3F8;
              transition: all 0.2s ease;
              vertical-align: middle;
            }
            .premium-table tr:nth-child(odd) td {
              background: #ffffff;
            }
            .premium-table tr:nth-child(even) td {
              background: #FAFBFD;
            }
            .premium-table tr:hover td {
              background: #F1F4F9;
            }
            .premium-table tr:last-child td {
              border-bottom: none;
            }
            
            .action-btn {
              width: 42px;
              height: 42px;
              border-radius: 10px;
              display: flex;
              justify-content: center;
              align-items: center;
              cursor: pointer;
              transition: all 0.2s ease;
              border: none;
            }
            .action-btn-view {
              background: rgba(75, 118, 230, 0.15);
              color: #4B76E6;
            }
            .action-btn-view:hover {
              background: #4B76E6 !important;
              color: #ffffff !important;
              box-shadow: 0 4px 12px rgba(75, 118, 230, 0.3);
            }
            .action-btn-edit {
              background: rgba(255, 159, 4, 0.15);
              color: #FF9F04;
            }
            .action-btn-edit:hover {
              background: #FF9F04 !important;
              color: #ffffff !important;
              box-shadow: 0 4px 12px rgba(255, 159, 4, 0.3);
            }
            .action-btn-delete {
              background: rgba(255, 89, 94, 0.15);
              color: #FF595E;
            }
            .action-btn-delete:hover {
              background: #FF595E !important;
              color: #ffffff !important;
              box-shadow: 0 4px 12px rgba(255, 89, 94, 0.3);
            }

            ::-webkit-scrollbar { width: 6px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: #DCE3EE; border-radius: 10px; }
            ::-webkit-scrollbar-thumb:hover { background: #A0A5B5; }

            .premium-filter-input {
              height: 44px !important;
              padding: 0 16px !important;
              border-radius: 8px !important;
              border: 1px solid #E2E6EF !important;
              font-size: 14px !important;
              width: 100% !important;
              background-color: #ffffff !important;
              transition: all 0.2s !important;
              outline: none !important;
              font-weight: 500 !important;
              color: #1B1B29 !important;
              box-sizing: border-box !important;
            }
            .premium-filter-input::placeholder {
              color: #999999 !important;
              font-weight: 500 !important;
              opacity: 1 !important;
            }
            .premium-filter-input:focus {
              border-color: #604BE8 !important;
              background-color: #fff !important;
              box-shadow: 0 0 0 4px rgba(96, 75, 232, 0.15) !important;
            }

            @keyframes fadeInScale {
              from { opacity: 0; transform: scale(0.95) translateY(10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
            .premium-card {
              animation: fadeInScale 0.4s cubic-bezier(0, 0, 0.2, 1);
            }
            `}
            </style>

            {/* Centered Permission Denied Modal */}
            {showPermModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(27, 27, 41, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 10000, backdropFilter: 'blur(8px)'
                }}>
                    <div style={{
                        backgroundColor: '#fff', padding: '30px', borderRadius: '20px', textAlign: 'center',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.15)', maxWidth: '400px', width: '90%', animation: 'fadeInScale 0.3s ease-out'
                    }}>
                        <div style={{ color: '#FF595E', fontSize: '3rem', marginBottom: '15px' }}>⚠️</div>
                        <h3 style={{ marginBottom: '10px', color: '#1B1B29', fontSize: '20px', fontWeight: '700' }}>Permission Denied</h3>
                        <p style={{ color: '#777777', marginBottom: '20px', fontSize: '14px', fontWeight: '500' }}>{permModalMsg}</p>
                        <button
                            onClick={() => setShowPermModal(false)}
                            style={{ background: '#604BE8', border: 'none', borderRadius: '10px', padding: '12px 30px', fontWeight: '600', color: '#fff', cursor: 'pointer', boxShadow: '0 4px 15px rgba(96, 75, 232, 0.3)' }}
                        >
                            Understood
                        </button>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Top Bar Section (Image 1 Header) */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    background: 'transparent',
                    padding: '36px 48px 16px 48px',
                    width: '100%',
                    boxSizing: 'border-box',
                    flex: '0 0 auto'
                }}>
                    {/* Left Side: Back Button */}
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                        <button 
                            onClick={() => navigate('/admin')}
                            style={{
                                height: '44px',
                                padding: '0 20px',
                                borderRadius: '10px',
                                border: '1px solid #E2E6EF',
                                background: '#ffffff',
                                color: '#604BE8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                fontWeight: '600',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.background = '#eff2f9'; }}
                            onMouseOut={(e) => { e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.background = '#ffffff'; }}
                        >
                            <FaArrowLeft style={{ fontSize: '12px' }} /> Back to Admin
                        </button>
                    </div>

                    {/* Center: Title */}
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                        <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#1B1B29', letterSpacing: '-0.5px' }}>Employee List</h1>
                    </div>

                    {/* Right Side: Filters & Action Buttons */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>


                        {canWrite && (
                            <button
                                onClick={() => navigate('/schedule-assign-employee')}
                                style={{
                                    height: '44px',
                                    padding: '0 16px',
                                    borderRadius: '8px',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #E2E6EF',
                                    color: '#4A4A5A',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = '#eff2f9'; e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.color = '#604BE8'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.color = '#4A4A5A'; }}
                            >
                                <FaCalendarCheck /> Assign Schedule
                            </button>
                        )}

                        <button
                            onClick={() => setShowColumnModal(true)}
                            style={{
                                height: '44px',
                                padding: '0 16px',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #E2E6EF',
                                color: '#4A4A5A',
                                fontWeight: '600',
                                fontSize: '14px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.background = '#eff2f9'; e.currentTarget.style.borderColor = '#604BE8'; e.currentTarget.style.color = '#604BE8'; }}
                            onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#E2E6EF'; e.currentTarget.style.color = '#4A4A5A'; }}
                        >
                            <FaFilter /> Manage Columns
                        </button>

                        <button
                            onClick={addNewEmployee}
                            style={{
                                height: '44px',
                                padding: '0 24px',
                                backgroundColor: '#604BE8',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '10px',
                                fontWeight: '600',
                                fontSize: '15px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                boxShadow: '0 4px 15px rgba(96, 75, 232, 0.25)',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#4B76E6'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#604BE8'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <FaPlus style={{ fontSize: '16px' }} /> Add New Employee
                        </button>
                    </div>
                </div>

                {/* Filter Bar & Search Row */}
                <div style={{ padding: "0 48px 24px 48px", flex: '0 0 auto' }}>
                    <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)', border: '1px solid #E2E6EF' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div 
                                onClick={() => setShowFilters(!showFilters)} 
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}
                            >
                                <span style={{ fontWeight: '700', fontSize: '15px', color: '#604BE8' }}>Employee Filters</span>
                                {showFilters ? <FaChevronUp style={{ color: '#604BE8', fontSize: '14px' }} /> : <FaChevronDown style={{ color: '#604BE8', fontSize: '14px' }} />}
                            </div>
                            {showFilters && (
                                <button onClick={clearFilters} style={{ background: '#f1f5f9', border: 'none', color: '#604BE8', fontWeight: '600', fontSize: '12px', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px' }}>Clear Filters</button>
                            )}
                        </div>
                        {showFilters && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '20px' }}>
                            {isGlobalMaster && companyOptions.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Company</label>
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <select
                                            className="premium-filter-input"
                                            value={selectedCompany}
                                            onChange={(e) => setSelectedCompany(e.target.value)}
                                            style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', cursor: 'pointer', paddingRight: '44px' }}
                                        >
                                            <option value="All">All Companies</option>
                                            {companyOptions.map((comp, idx) => (
                                                <option key={idx} value={comp}>{comp}</option>
                                            ))}
                                        </select>
                                        <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                                            <FaChevronDown />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(isGlobalMaster || (isCompanyAdmin && !localStorage.getItem('branch_name'))) && availableBranches.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Branch</label>
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <select
                                            className="premium-filter-input"
                                            value={selectedBranch}
                                            onChange={(e) => setSelectedBranch(e.target.value)}
                                            style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', cursor: 'pointer', paddingRight: '44px' }}
                                        >
                                            <option value="">All Branches</option>
                                            {availableBranches.map((branch, idx) => (
                                                <option key={idx} value={branch}>{branch}</option>
                                            ))}
                                        </select>
                                        <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                                            <FaChevronDown />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Name</label>
                                <input
                                    type="text"
                                    className="premium-filter-input"
                                    placeholder="Search by name..."
                                    value={filters.name}
                                    onChange={(e) => handleFilterChange('name', e.target.value)}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Employee ID</label>
                                <input
                                    type="text"
                                    className="premium-filter-input"
                                    placeholder="Search by ID..."
                                    value={filters.employeeId}
                                    onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Designation</label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <select
                                        className="premium-filter-input"
                                        value={filters.designation}
                                        onChange={(e) => handleFilterChange('designation', e.target.value)}
                                        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', cursor: 'pointer', paddingRight: '44px' }}
                                    >
                                        <option value="">All Designations</option>
                                        {employeeDesignations.map((des) => (
                                            <option key={des.id} value={des.name}>{des.name}</option>
                                        ))}
                                    </select>
                                    <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                                        <FaChevronDown />
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Type</label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <select
                                        className="premium-filter-input"
                                        value={filters.type}
                                        onChange={(e) => handleFilterChange('type', e.target.value)}
                                        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', cursor: 'pointer', paddingRight: '44px' }}
                                    >
                                        <option value="">All Types</option>
                                        {employeeTypes.map((typ) => (
                                            <option key={typ.id} value={typ.name}>{typ.name}</option>
                                        ))}
                                    </select>
                                    <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                                        <FaChevronDown />
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Phone Number</label>
                                <input
                                    type="text"
                                    className="premium-filter-input"
                                    placeholder="Search by phone..."
                                    value={filters.phone}
                                    onChange={(e) => handleFilterChange('phone', e.target.value)}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Salary</label>
                                <input
                                    type="text"
                                    className="premium-filter-input"
                                    placeholder="Search by salary..."
                                    value={filters.salary}
                                    onChange={(e) => handleFilterChange('salary', e.target.value)}
                                />
                            </div>
                        </div>
                        )}
                    </div>
                </div>

                {/* Scrollable Data Area */}
                <div style={{ flex: 1, overflowY: 'auto', padding: "0 48px 48px" }}>
                    
                    

                    {filteredEmployees.length === 0 ? (
                        <div style={{ background: '#ffffff', borderRadius: '10px', padding: '80px 40px', textAlign: 'center', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)', border: '1px solid #E2E6EF' }} className="premium-card">
                            <div style={{ fontSize: '54px', marginBottom: '20px' }}>👤</div>
                            <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#1B1B29', margin: '0 0 10px', letterSpacing: '-0.5px' }}>No Employees Found</h3>
                            <p style={{ color: '#777777', marginBottom: '30px', maxWidth: '400px', margin: '0 auto 30px', lineHeight: '1.6', fontSize: '14px' }}>No records match your current directory filters. Try adjusting your search criteria or company filters.</p>
                            <button onClick={clearFilters} style={{ padding: '14px 32px', background: '#604BE8', color: '#ffffff', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 15px rgba(96, 75, 232, 0.25)' }}>
                                Clear Filters
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            maxHeight: '68vh', overflowY: 'auto', borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)',
                            width: '100%', backgroundColor: '#ffffff', border: 'none'
                        }} className="premium-card">
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        {columnOrder.map((col, index) => (
                                            <th
                                                key={col.key}
                                                style={{ textAlign: col.align }}
                                                draggable={col.key !== "actions"}
                                                onDragStart={(e) => col.key !== "actions" && handleDragStart(e, index)}
                                                onDragOver={(e) => col.key !== "actions" && handleDragOver(e)}
                                                onDragLeave={(e) => col.key !== "actions" && handleDragLeave(e)}
                                                onDrop={(e) => col.key !== "actions" && handleDrop(e, index)}
                                                onDragEnd={(e) => col.key !== "actions" && handleDragEnd(e)}
                                                onDoubleClick={(e) => {
                                                    e.stopPropagation();
                                                    if (col.key !== "actions") removeColumn(index);
                                                }}
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEmployees.map((emp, index) => (
                                        <tr
                                            key={emp._id || index}
                                            onClick={() => handleEmployeeClick(emp)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {columnOrder.map((col) => {
                                                const baseCellStyle = { textAlign: col.align };
                                                if (col.key === 'address') {
                                                    baseCellStyle.minWidth = '200px';
                                                    baseCellStyle.whiteSpace = 'normal';
                                                }
                                                if (col.key === 'specialDays') {
                                                    baseCellStyle.minWidth = '250px';
                                                    baseCellStyle.whiteSpace = 'normal';
                                                }
                                                
                                                if (col.key === 'actions') {
                                                    return (
                                                        <td key={col.key} style={{ paddingRight: '24px' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                                                <button onClick={(e) => { e.stopPropagation(); handleViewDetails(emp); }} className="action-btn action-btn-view" title="View Details">
                                                                    <FaEye style={{ fontSize: '20px' }} />
                                                                </button>
                                                                <button onClick={(e) => { e.stopPropagation(); handleEditEmployee(emp); }} className="action-btn action-btn-edit" title="Edit Profile">
                                                                    <FaEdit style={{ fontSize: '20px' }} />
                                                                </button>
                                                                <button onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(emp.employeeId || emp._id); }} className="action-btn action-btn-delete" title="Delete Profile">
                                                                    <FaTrash style={{ fontSize: '20px' }} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                const content = getCellContent(emp, col);
                                                return (
                                                    <td key={col.key} style={baseCellStyle}>
                                                        {content}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Employee Details Modal */}
            {showDetailsModal && detailsEmployee && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(27, 27, 41, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 10000,
                        backdropFilter: 'blur(8px)'
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowDetailsModal(false); }}
                >
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '20px',
                        width: '900px',
                        maxWidth: '95%',
                        maxHeight: '90vh',
                        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        animation: 'fadeInScale 0.3s ease-out',
                        color: '#1B1B29'
                    }}>
                        {/* Modal Header */}
                        <div style={{ background: '#604BE8', padding: '36px 28px', textAlign: 'center', position: 'relative' }}>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                style={{ position: 'absolute', top: '20px', right: '20px', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#ffffff', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            >
                                <FaTimes />
                            </button>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                                {detailsEmployee.profileImage ? (
                                    <img src={detailsEmployee.profileImage} alt="Profile" style={{ width: '100px', height: '100px', borderRadius: '20px', border: '3px solid white', objectFit: 'cover', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }} />
                                ) : (
                                    <div style={{ width: '80px', height: '80px', background: '#ffffff', color: '#604BE8', borderRadius: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '32px', fontWeight: '700', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}>
                                        {detailsEmployee.name ? detailsEmployee.name.charAt(0).toUpperCase() : <FaUserTie />}
                                    </div>
                                )}
                                <div style={{ textAlign: 'center' }}>
                                    <h2 style={{ margin: 0, color: '#ffffff', fontSize: '24px', fontWeight: '700' }}>{detailsEmployee.name}</h2>
                                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: '4px 0 0', fontSize: '15px', fontWeight: '500' }}>{detailsEmployee.employeeId} | {detailsEmployee.employeeDesignation}</p>
                                </div>
                            </div>
                        </div>
                        {/* Modal Body */}
                        <div style={{ padding: '32px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '28px' }}>
                                {/* Basic Info Card */}
                                <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                                    <label style={{ display: 'block', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                        <FaUserTie style={{ marginRight: '8px' }} /> Basic Information
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Phone</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.phoneNumber}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Email</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.email}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Gender</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.gender || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Status</span> <p style={{ margin: '2px 0 0' }}><span style={{ padding: '3px 8px', borderRadius: '6px', background: detailsEmployee.status === 'Active' ? '#d4edda' : '#f8d7da', color: detailsEmployee.status === 'Active' ? '#155724' : '#721c24', fontSize: '12px', fontWeight: '700' }}>{detailsEmployee.status}</span></p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Date of Birth</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.dateOfBirth || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Joining Date</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.dateOfJoining || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Company</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.company}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Type</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.employeeType}</p></div>
                                    </div>
                                </div>
                                {/* Personal & Address */}
                                <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                                    <label style={{ display: 'block', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                        <FaUserTie style={{ marginRight: '8px' }} /> Personal Details
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Salutation</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.salutation || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Marital Status</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.maritalStatus || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Nationality</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.nationality || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>ID Number</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.idNumber || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>ID Expiry</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.idExpiry || 'N/A'}</p></div>
                                    </div>
                                    <div style={{ marginTop: '15px' }}>
                                        <span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Address</span>
                                        <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px', whiteSpace: 'pre-wrap' }}>{detailsEmployee.address}</p>
                                    </div>
                                </div>
                                {/* Financial Info */}
                                <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                                    <label style={{ display: 'block', color: '#06D6A0', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                        <FaUserTie style={{ marginRight: '8px' }} /> Financial Details
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', background: '#ffffff', padding: '12px', borderRadius: '8px', border: '1px solid #E2E6EF' }}>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Basic Salary</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '700', fontSize: '15px' }}>{currency}{detailsEmployee.basicSalary || '0'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>HRA</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{currency}{detailsEmployee.hra || '0'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>TA</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{currency}{detailsEmployee.ta || '0'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>OA</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{currency}{detailsEmployee.oa || '0'}</p></div>
                                        <div style={{ gridColumn: 'span 2', borderTop: '1px solid #E2E6EF', paddingTop: '8px', marginTop: '4px' }}>
                                            <span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Total Salary</span> <p style={{ margin: '2px 0 0', color: '#06D6A0', fontWeight: '800', fontSize: '18px' }}>{currency}{detailsEmployee.totalSalary || '0'}</p>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '15px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Bank Name</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.bankName || 'N/A'}</p></div>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Branch Code</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.branchCode || 'N/A'}</p></div>
                                            <div style={{ gridColumn: 'span 2' }}><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Account Number</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.accountNumber || 'N/A'}</p></div>
                                            <div style={{ gridColumn: 'span 2' }}><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Account Holder</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.accountHolderName || 'N/A'}</p></div>
                                        </div>
                                    </div>
                                </div>
                                {/* NEW: Delivery Profile Section */}
                                {detailsEmployee.deliveryProfile && (
                                    <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                                        <label style={{ display: 'block', color: '#FF9F04', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                            <FaTruck style={{ marginRight: '8px' }} /> Delivery Profile
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Delivery ID</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.deliveryProfile.employeeId || 'N/A'}</p></div>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Role</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.deliveryProfile.role || 'N/A'}</p></div>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Phone</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.deliveryProfile.phoneNumber || 'N/A'}</p></div>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Email</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.deliveryProfile.email || 'N/A'}</p></div>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Vehicle Number</span> <p style={{ margin: '2px 0 0', color: '#06D6A0', fontWeight: '700', fontSize: '15px' }}>{detailsEmployee.deliveryProfile.vehicleNumber || 'N/A'}</p></div>
                                            <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Secret Key</span> <p style={{ margin: '2px 0 0', color: '#FF595E', fontWeight: '700', fontSize: '15px' }}>{detailsEmployee.deliveryProfile.secretKey || 'N/A'}</p></div>
                                        </div>
                                    </div>
                                )}
                                {/* Schedule & Others */}
                                <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF' }}>
                                    <label style={{ display: 'block', color: '#FF9F04', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                        <FaClock style={{ marginRight: '8px' }} /> Schedule & Other
                                    </label>
                                    <div style={{ marginBottom: '15px' }}>
                                        <span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Shift Timing</span>
                                        <div style={{ margin: '2px 0 0' }}>
                                            {(() => {
                                                const assign = assignments.find(a => (String(a.employee_id) === String(detailsEmployee._id || detailsEmployee.id) || String(a.employee_id) === String(detailsEmployee.employeeId)) && (a.is_active === true || String(a.is_active) === 'true'));
                                                if (!assign) return <span style={{ color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>No assigned schedule</span>;
                                                const rule = scheduleRules.find(r => String(r._id || r.id) === String(assign.schedule_id));
                                                if (!rule) return <span style={{ color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>Schedule rule missing</span>;

                                                let shift = null;
                                                if (rule.shift_id) {
                                                    shift = shifts.find(s => String(s._id || s.id) === String(rule.shift_id));
                                                }
                                                if (!shift) {
                                                    shift = rule;
                                                }
                                                if (!shift) return <span style={{ color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>Shift missing</span>;

                                                const timingStr = getShiftTimingString(shift);
                                                return (
                                                    <div style={{ whiteSpace: 'normal' }}>
                                                        <div style={{ fontWeight: '700', color: '#1B1B29', fontSize: '15px' }}>{timingStr}</div>
                                                        <div style={{ fontSize: '13px', color: '#777777' }}>{rule.schedule_name}</div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    {detailsEmployee.specialTimings && detailsEmployee.specialTimings.length > 0 && (
                                        <div style={{ marginBottom: '15px' }}>
                                            <span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Special Timings</span>
                                            <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
                                                {detailsEmployee.specialTimings.map((st, idx) => (
                                                    <li key={idx} style={{ fontSize: '14px', color: '#1B1B29', fontWeight: '500' }}>{st.date}: {st.startTime} - {st.endTime} ({st.status})</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {(() => {
                                        const assign = assignments.find(a => String(a.employee_id) === String(detailsEmployee._id) && a.is_active);
                                        if (assign && assign.special_day_assignments && assign.special_day_assignments.length > 0) {
                                            return (
                                                <div style={{ marginBottom: '15px' }}>
                                                    <span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Special Days & Exceptions</span>
                                                    <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
                                                        {assign.special_day_assignments.map((sd, idx) => (
                                                            <li key={idx} style={{ fontSize: '14px', marginBottom: '8px' }}>
                                                                <div style={{ fontWeight: '700', color: '#1B1B29' }}>
                                                                    {sd.date} <span style={{ fontWeight: '500', color: '#777777' }}>({sd.type})</span>
                                                                </div>
                                                                <div style={{ color: '#4A4A5A' }}>{sd.description}</div>
                                                                {sd.type === 'Half-Day' && <div style={{ fontSize: '13px', color: '#FF9F04', fontWeight: '600' }}><FaClock style={{ fontSize: '11px' }} /> {sd.start_time} - {sd.end_time}</div>}
                                                                {sd.type === 'Extended' && <div style={{ fontSize: '13px', color: '#604BE8', fontWeight: '600' }}><FaClock style={{ fontSize: '11px' }} /> {sd.extended_start} - {sd.extended_end}</div>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Education</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.education || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Experience</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.previousExperience || 'N/A'}</p></div>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Skills</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>{detailsEmployee.skills || 'N/A'}</p></div>
                                    </div>
                                </div>
                                {/* System Credentials */}
                                <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF', gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'block', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                        <FaUserTie style={{ marginRight: '8px' }} /> System Credentials
                                    </label>
                                    <div style={{ display: 'flex', gap: '30px' }}>
                                        <div><span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Username</span> <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '15px' }}>{detailsEmployee.username}</p></div>
                                    </div>
                                </div>

                                {/* Dynamic Doctypes Fields */}
                                {doctypeFields.filter(f => !['name', 'employee_id', 'phone_number', 'email', 'gender', 'date_of_birth', 'date_of_joining', 'status', 'salutation', 'marital_status', 'id_number', 'id_expiry', 'address', 'employee_designation', 'employee_type', 'department', 'basic_salary', 'hra', 'ta', 'oa', 'total_salary', 'username', 'password', 'bank_name', 'account_holder_name', 'account_number', 'branch_code', 'nationality', 'education', 'previous_experience', 'skills', 'health_info', 'family_details', 'profile_image'].includes(f.id)).length > 0 && (
                                    <div style={{ background: '#F8F9FD', padding: '20px', borderRadius: '12px', border: '1px solid #E2E6EF', gridColumn: '1 / -1' }}>
                                        <label style={{ display: 'block', color: '#06D6A0', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                            Custom Information
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
                                            {doctypeFields.filter(f => !['name', 'employee_id', 'phone_number', 'email', 'gender', 'date_of_birth', 'date_of_joining', 'status', 'salutation', 'marital_status', 'id_number', 'id_expiry', 'address', 'employee_designation', 'employee_type', 'department', 'basic_salary', 'hra', 'ta', 'oa', 'total_salary', 'username', 'password', 'bank_name', 'account_holder_name', 'account_number', 'branch_code', 'nationality', 'education', 'previous_experience', 'skills', 'health_info', 'family_details', 'profile_image'].includes(f.id)).map(f => (
                                                <div key={f.id}>
                                                    <span style={{ color: '#777777', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>{f.label}</span>
                                                    <p style={{ margin: '2px 0 0', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}>
                                                        {f.type === 'Check' ? (detailsEmployee[f.id] ? 'Yes' : 'No') : (detailsEmployee[f.id] || 'N/A')}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Modal Footer */}
                        <div style={{ padding: '20px 28px', borderTop: '1px solid #E2E6EF', background: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            {canWrite && (
                                <button
                                    onClick={() => {
                                        setShowDetailsModal(false);
                                        handleEditEmployee(detailsEmployee);
                                    }}
                                    style={{
                                        padding: '12px 24px',
                                        background: '#604BE8',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        boxShadow: '0 4px 15px rgba(96, 75, 232, 0.25)'
                                    }}
                                >
                                    <FaEdit /> Edit Details
                                </button>
                            )}
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                style={{
                                    padding: '12px 24px',
                                    background: '#F1F4F9',
                                    color: '#1B1B29',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(27, 27, 41, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 10001, backdropFilter: 'blur(8px)'
                }} onClick={closeDeleteConfirm}>
                    <div style={{
                        backgroundColor: '#ffffff', borderRadius: '20px', width: '420px', maxWidth: '90%',
                        padding: '36px', boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)', textAlign: 'center',
                        animation: 'fadeInScale 0.3s ease-out', color: '#1B1B29'
                    }}>
                        <div style={{ width: '70px', height: '70px', background: '#fee2e2', color: '#FF595E', borderRadius: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px', margin: '0 auto 20px' }}>
                            <FaTrash />
                        </div>
                        <h3 style={{ color: '#1B1B29', fontSize: '22px', fontWeight: '700', marginBottom: '10px' }}>Confirm Delete</h3>
                        <p style={{ color: '#777777', fontSize: '15px', marginBottom: '30px', lineHeight: '1.6', fontWeight: '500' }}>Are you sure you want to delete this employee? This action cannot be undone.</p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={closeDeleteConfirm} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={confirmDeleteEmployee} disabled={loading} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#FF595E', color: '#ffffff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(255, 89, 94, 0.3)' }}>{loading ? 'Deleting...' : 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Column Management Modal */}
            {showColumnModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(27, 27, 41, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 10000, backdropFilter: 'blur(8px)'
                }} onClick={(e) => { if (e.target === e.currentTarget) setShowColumnModal(false); }}>
                    <div style={{
                        backgroundColor: '#ffffff', borderRadius: '20px', width: '480px', maxWidth: '90%',
                        padding: '32px', boxShadow: '0 20px 50px rgba(0,0,0,0.15)', animation: 'fadeInScale 0.3s ease-out', color: '#1B1B29'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #F1F3F8', paddingBottom: '16px' }}>
                            <h4 style={{ margin: 0, color: '#1B1B29', fontSize: '20px', fontWeight: '700' }}>Manage Table Columns</h4>
                            <button onClick={() => setShowColumnModal(false)} style={{ background: '#F1F4F9', border: 'none', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: '#1B1B29', transition: 'all 0.2s' }}><FaTimes /></button>
                        </div>
                        
                        <div style={{ padding: '0 10px', overflowY: 'auto', maxHeight: '50vh' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1B1B29', fontSize: '14px' }}>Add New Column</label>
                                <select value={selectedFieldToAdd} onChange={(e) => setSelectedFieldToAdd(e.target.value)} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E2E6EF', borderRadius: '10px', fontSize: '14px', background: '#F8F9FD', color: '#1B1B29', outline: 'none', marginBottom: '12px' }}>
                                    <option value="">Choose a field...</option>
                                    {possibleColumns.filter((p) => !columnOrder.some((c) => c.key === p.key)).map((p) => (
                                        <option key={p.key} value={p.key}>{p.label}</option>
                                    ))}
                                </select>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#1B1B29', fontSize: '14px' }}>Insert Position</label>
                                <select value={selectedPosition} onChange={(e) => setSelectedPosition(Number(e.target.value))} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E2E6EF', borderRadius: '10px', fontSize: '14px', background: '#F8F9FD', color: '#1B1B29', outline: 'none', marginBottom: '16px' }}>
                                    {Array.from({ length: columnOrder.length + 1 }, (_, i) => (
                                        <option key={i} value={i}>{i === columnOrder.length ? 'At the end' : `Before "${columnOrder[i].label}"`}</option>
                                    ))}
                                </select>
                                <button onClick={addColumn} disabled={!selectedFieldToAdd} style={{ width: '100%', padding: '14px', background: '#604BE8', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 4px 15px rgba(96, 75, 232, 0.3)' }}>Add Column</button>
                            </div>
                            
                            <div>
                                <h5 style={{ color: '#1B1B29', marginBottom: '12px', fontWeight: '700', fontSize: '15px' }}>Current Columns</h5>
                                {columnOrder.map((col, index) => (
                                    <div key={col.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '12px 16px', border: '1px solid #E2E6EF', borderRadius: '10px', backgroundColor: '#ffffff' }}>
                                        <span style={{ fontWeight: '600', color: '#4A4A5A', fontSize: '14px' }}>{col.label}</span>
                                        <button onClick={() => removeColumn(index)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#FF595E', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>Remove</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        
            {/* Success and Error Modals */}
            {message && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', animation: 'fadeInScale 0.3s ease-out' }}>
                        <div style={{ width: '60px', height: '60px', background: '#dcfce7', color: '#16a34a', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', margin: '0 auto 15px' }}>
                            <FaCheckCircle />
                        </div>
                        <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Success</h3>
                        <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>{message}</p>
                        <button onClick={() => setMessage('')} style={{ padding: '10px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Awesome!</button>
                    </div>
                </div>
            )}
            
            {error && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', animation: 'fadeInScale 0.3s ease-out' }}>
                        <div style={{ width: '60px', height: '60px', background: '#fee2e2', color: '#e74c3c', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', margin: '0 auto 15px' }}>
                            <FaExclamationTriangle />
                        </div>
                        <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Error</h3>
                        <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>{error}</p>
                        <button onClick={() => setError('')} style={{ padding: '10px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );

};
export default EmployeeList;
